// src/encounter/encounter-leave-guard.tsx
//
// D-73 (design desk, 6 Oct 2026, amended) · frame v2 only: the IN-APP half of the leave prompt. (The browser half,
// tab close / reload, is `use-leave-guard.ts`.) Navigating away from the encounter inside the app — a router
// navigation or Back — while the note has an unsaved, saving or failed save, or a changed dialog draft:
//   1. FLUSH first: every inline draft is committed and the note's pending save is sent now, so text typed a moment
//      before Back is saved, not lost;
//   2. WAIT for that save (at most `settleMs`): it succeeds and nothing else is unsaved → the navigation goes on,
//      with no prompt (nothing is pending any more);
//   3. otherwise ASK — the DS confirm "Discard this text?", "Keep editing" (default focus) and "Discard": the save
//      failed, it did not finish in time, the note cannot be saved now (signed, a load or refusal latch), or a
//      dialog holds a changed draft (a dialog draft is saved only by its own Save).
// A change of search params on the same encounter is not a leave (the step is page state, never in the URL: the only
// param the page writes is the workspace `tab`), with one exception: a tab that takes the note off
// screen (Chart, Documents — by a click or by browser Back / Forward) while a filled phrase is open. A phrase lives
// only in its line until Complete, so that tab change drops it; it asks at once (nothing to flush) and Discard keeps
// the visit's note session. The other in-page changes that drop a phrase (Back to the pre-encounter, a centre panel,
// another section) never touch the URL: the page holds them through `confirmNoteViewChange` and this dialog answers
// them too (#689 follow-up, Andrew 10 Oct). Mounted by the encounter route only with frame v2 on, and it blocks only
// while the page says frame v2 is active.
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useBlocker, useLocation, type BlockerFunction } from 'react-router-dom';
import { ModalYesNo } from '@scripsteam/scrips-signal-ds';
import { DISCARD_TITLE } from './lib/discard-draft';
import { ENCOUNTER_TOP_TAB_PARAM } from './pre-encounter-context-strip';
import { NoteSessionContext, discardNoteSession } from './services/note-session';
import {
  flushInlineDrafts,
  flushNote,
  focusedNoteField,
  hasPhraseDraft,
  hasUnsavedNote,
  inlineFlushFailed,
  settleNoteViewChange,
  useUnsavedNote,
  type UnsavedNoteState,
} from './services/unsaved-note';

/** The line under the title when an open phrase is what leaving would drop (a phrase is not in the note until Complete). */
export const PHRASE_DRAFT_LEAVE_MESSAGE = 'You have an unfinished phrase. It is not in the note until you select Complete.';

/** How long a blocked navigation waits for the flushed save before asking. */
export const LEAVE_SETTLE_MS = 5000;

/** Does this URL's workspace tab show the note? (No tab param is the Encounter tab; Chart, Documents and Terrain replace it, and so does an old `timeline` link, which opens the Chart.) */
export function showsNote(search: string): boolean {
  const tab = new URLSearchParams(search).get(ENCOUNTER_TOP_TAB_PARAM);
  // `terrain` replaces the note only while its switch is on; off, the page shows the Encounter tab, so the guard stays.
  return tab !== 'chart' && tab !== 'timeline' && tab !== 'documents' && !(tab === 'terrain' && isEncounterFlagOn('terrain'));
}

export function EncounterLeaveGuard({ settleMs = LEAVE_SETTLE_MS }: { settleMs?: number }) {
  const shouldBlock = useCallback<BlockerFunction>(({ currentLocation, nextLocation }) => {
    const s = useUnsavedNote.getState();
    if (!s.active) return false;
    if (currentLocation.pathname === nextLocation.pathname) {
      // Same visit: only a filled phrase is lost, and only when the new tab takes the note off screen.
      return hasPhraseDraft(s.drafts) && showsNote(currentLocation.search) && !showsNote(nextLocation.search);
    }
    // An inline draft held for a moment (or a focused field that commits on leave) may be the only unsaved text.
    const inline = flushInlineDrafts() || !!focusedNoteField();
    return inline || hasUnsavedNote(s);
  }, []);
  const blocker = useBlocker(shouldBlock);
  const location = useLocation();
  // A blocked tab change on this visit (see the header): nothing to flush, and Discard does not end the note session.
  const sameVisit = blocker.state === 'blocked' && blocker.location.pathname === location.pathname;
  // A held in-page change (Back to the pre-encounter, a centre panel, another section) waiting for an answer.
  const viewChange = useUnsavedNote((s) => s.pendingViewChange !== null);
  const noteSession = useContext(NoteSessionContext);
  const blockerRef = useRef(blocker);
  useEffect(() => {
    blockerRef.current = blocker;
  });
  const [ask, setAsk] = useState(false);
  // An unfinished phrase is said by name: "Discard this text?" alone does not tell the physician what would go.
  const phraseDraft = useUnsavedNote((s) => hasPhraseDraft(s.drafts));

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    // A navigation (browser Back, a link) while an in-page change waits for its answer: the navigation's own question
    // replaces it, so one Discard answers once — the held change is dropped, never run after the page has gone.
    if (useUnsavedNote.getState().pendingViewChange) settleNoteViewChange(false);
    if (sameVisit) {
      // Asked at once, via a timeout: state is never set in the effect body itself.
      const now = setTimeout(() => setAsk(true), 0);
      return () => clearTimeout(now);
    }
    let done = false;
    let sawSaving = false;
    let canSaveAtFlush = true;
    let restart: ReturnType<typeof setTimeout> | undefined;
    const settle = (decision: 'ask' | 'proceed') => {
      if (done) return;
      if (decision === 'proceed' && flushInlineDrafts()) {
        // Text typed WHILE the save was in flight (Codex P1, 6 Oct): commit and save it too before leaving. The next
        // cycle sends it (once — the subscription must not send it as well).
        sawSaving = false;
        if (restart === undefined) restart = setTimeout(() => {
          restart = undefined;
          cycle();
        }, 0);
        return;
      }
      done = true;
      if (decision === 'ask') setAsk(true);
      else blockerRef.current.proceed?.();
    };
    // 3. Decide from what the page publishes. A `failed` left over from before the flush does not decide until
    //    the flushed save has been seen in flight (or the page could not send one).
    const decide = (s: UnsavedNoteState) => {
      if (done) return;
      if (s.saving) {
        sawSaving = true;
        return;
      }
      if (inlineFlushFailed() || Object.keys(s.drafts).length > 0) settle('ask');
      else if (s.failed && (sawSaving || !canSaveAtFlush)) settle('ask');
      else if (s.dirty && !s.canSave) settle('ask');
      else if (s.dirty && sawSaving) {
        // An edit landed while the save was in flight: send it now rather than wait for the autosave.
        sawSaving = false;
        flushNote();
      } else if (!s.dirty && !s.failed) settle('proceed');
    };
    // 1–2. Outside the effect (flushSync cannot run inside one): commit the inline drafts and let the page render
    //      them, then send the note's pending save — it now carries the text typed just before leaving.
    function cycle() {
      if (done) return;
      flushSync(() => {
        flushInlineDrafts();
      });
      sawSaving = false;
      canSaveAtFlush = useUnsavedNote.getState().canSave;
      flushNote();
      decide(useUnsavedNote.getState());
    }
    const unsubscribe = useUnsavedNote.subscribe((s) => decide(s));
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      setAsk(true);
    }, settleMs);
    const start = setTimeout(() => {
      setAsk(false);
      cycle();
    }, 0);
    return () => {
      done = true;
      unsubscribe();
      clearTimeout(timer);
      clearTimeout(start);
      if (restart) clearTimeout(restart);
    };
  }, [blocker.state, sameVisit, settleMs]);

  return (
    <ModalYesNo
      open={viewChange || (blocker.state === 'blocked' && ask)}
      title={DISCARD_TITLE}
      message={phraseDraft ? PHRASE_DRAFT_LEAVE_MESSAGE : null}
      cancelLabel="Keep editing"
      confirmLabel="Discard"
      destructive
      onCancel={() => {
        if (viewChange) {
          settleNoteViewChange(false);
          return;
        }
        setAsk(false);
        blocker.reset?.();
      }}
      onConfirm={() => {
        if (viewChange) {
          // Still on the visit: only the phrase goes (it unmounts with the change); the note session stays.
          settleNoteViewChange(true);
          return;
        }
        setAsk(false);
        // PROD-3107: before leaving, refuse every later write of this visit's note — a save paused offline would
        // otherwise resume on reconnect and write the text the clinician just discarded. A tab change stays on it.
        if (!sameVisit) discardNoteSession(noteSession);
        blocker.proceed?.();
      }}
    />
  );
}
